import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import React, { useState } from "react";
import * as Yup from "yup";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import queryGraphql from "../../../helpers/ApiInterface";
import { useAuth } from "../../../helpers/AuthProvider";
import { Close } from "@mui/icons-material";
import { Formik, ErrorMessage, Form } from "formik";

import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import MiniBot from "../../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";

// --- Interfaces ---
interface SolutionOwnerInfo {
    name: string;
}

interface DataSourcesNestedDetails {
    // User-submitted fields
    projectId: string;
    location: string;
    lake: string;
    zone: string;
    description: string;
    resourceSpec: {
        type: string;
        name: string;
    };
    discoverySpec: {
        enabled: boolean;
        csvOptions?: {
            headerRows: number;
            delimiter: string;
        };
    };
}

interface DataSourceDetailsData {
    id: string;
    name: string;
    solutionCategoryType: string;
    status: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    details: {
        dataSources: DataSourcesNestedDetails;
    };
    solutionOwner: SolutionOwnerInfo;
    reasonForRejection?: string;
}

// --- Validation Schemas ---
// Approval no longer requires input, so no schema needed for approval.

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
});

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

// --- Main Content ---
const DataSourcesApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [dataSource, setDataSource] = useState<DataSourceDetailsData | null>(null);
    const [solutionEndpoint, setSolutionEndpoint] = useState<string | null>(null);
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [dynamicLabels, setDynamicLabels] = useState({
        projectId: 'Project ID',
    });
    const fallback = '-';

    React.useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                const data = await queryGraphql(
                    `
                    query GetDataSource($getDataSourceId: String!) {
                      getDataSource(id: $getDataSourceId) {
                        id, name, solutionCategoryType, status, reasonForRejection, cloudProvider
                        details { 
                          dataSources { 
                            projectId, location, lake, zone, description,
                            resourceSpec { type, name },
                            discoverySpec { enabled, csvOptions { headerRows, delimiter } },
                          } 
                        }
                        solutionOwner { id, name }
                      }
                    }
                    `, { getDataSourceId: id }
                );
                if (data.getDataSource) {
                    setDataSource(data.getDataSource);
                } else {
                    console.log("Data Source details not found.");
                    setDataSource(null);
                }
            } catch (error) {
                console.log("Error fetching Data Source details:", error);
            }
        };
        fetchData();
    }, [id]);

    React.useEffect(() => {
        if (dataSource?.cloudProvider) {
            switch (dataSource.cloudProvider) {
                case 'aws':
                    setDynamicLabels({ projectId: 'Account ID' });
                    break;
                case 'azure':
                    setDynamicLabels({ projectId: 'Subscription ID' });
                    break;
                case 'gcp':
                default:
                    setDynamicLabels({ projectId: 'Project ID' });
                    break;
            }
        }
    }, [dataSource]);


    React.useEffect(() => {
        const fetchEndpointUrl = async () => {
            if (!dataSource?.id) return;
            try {
                const data = await queryGraphql(
                    `query ResourceStatus($resourceStatusId: String!) {
                      resourceStatus(id: $resourceStatusId) { id, solutionEndpointUrl }
                    }`, { resourceStatusId: dataSource.id }
                );
                if (data.resourceStatus?.solutionEndpointUrl) {
                    setSolutionEndpoint(data.resourceStatus.solutionEndpointUrl);
                }
            } catch (error) {
                console.log("Error fetching solution endpoint URL:", error);
            }
        };
        fetchEndpointUrl();
    }, [dataSource]);

    const statusDisplay = dataSource?.status ? dataSource.status : "Status Unknown";
    const statusBgColor = dataSource?.status === "active" || dataSource?.status === "approved" ? "#12B76A10" : dataSource?.status === "rejected" ? "#F0443810" : "#FDB02210";
    const statusIcon = dataSource?.status === "active" || dataSource?.status === "approved" ? "/assets/tickcircle.svg" : dataSource?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg";

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/datasolutions/requests/data-sources" onClick={(e) => { e.preventDefault(); navigate("/datasolutions/requests/data-sources") }}>
                        Data Source Requests
                    </Link>
                    <Typography color="text.primary">Data Source Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/requests/data-sources")} >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">Data Source Details</Typography>
                </Box>
            </Box>
            <Grid container justifyContent="center">
                <Grid item xs={11} md={11} lg={11}>
                    {dataSource && (
                        <Formik
                            initialValues={{}} // No initial values needed
                            onSubmit={async () => {
                                // No payload details needed for approval anymore
                                try {
                                    const approvalData = await queryGraphql(
                                        `mutation ApproveSolutionDataSources($input: ApproveSolutionInput!) { approveSolutionDataSources(input: $input) { id name status } }`,
                                        { input: { id: dataSource.id } }
                                    );
                                    if (approvalData.approveSolutionDataSources?.id) {
                                        setOpenApproveModal(false);
                                        navigate("/datasolutions/requests/data-sources");
                                    } else {
                                        console.error("Approval failed", approvalData);
                                    }
                                } catch (error) {
                                    console.error("Error during approval:", error);
                                }
                            }}
                        >
                            {({ }) => (
                                <Form id="approve-dataSource-main-form">
                                    <Box my={2}>
                                        <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                                                <Box sx={{ mb: { xs: 1, sm: 0 } }}>
                                                    <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{dataSource.name || fallback}</Typography>
                                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${dataSource.solutionOwner?.name || fallback}`}</Typography>
                                                </Box>
                                                <Box textAlign="right">
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'flex-end' }}>
                                                        <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
                                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{ textTransform: 'capitalize' }}>{statusDisplay}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                        
                                        <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                                            <Grid container spacing={2.5}>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.projectId} value={dataSource.details.dataSources.projectId} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Location" value={(dataSource.details.dataSources.location)} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Lake" value={dataSource.details.dataSources.lake} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Zone" value={dataSource.details.dataSources.zone} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Cloud Provider" value={dataSource.cloudProvider?.toUpperCase()} /></Grid>
                                                {solutionEndpoint && (<Grid item xs={12} sm={6} md={3}><Typography variant="caption" display="block" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}>Solution Endpoint</Typography><Tooltip title={<Link href={solutionEndpoint} target="_blank" rel="noopener noreferrer" sx={{ color: '#ffffff' }}>{solutionEndpoint}</Link>}><Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, cursor: 'pointer' }} /></Tooltip></Grid>)}
                                                
                                                <Grid item xs={12}><Divider/></Grid>
                                                
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Resource Type" value={dataSource.details.dataSources.resourceSpec?.type} /></Grid>
                                                <Grid item xs={12} sm={6} md={9}><DetailItem label="Resource Name" value={dataSource.details.dataSources.resourceSpec?.name} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Discovery" value={dataSource.details.dataSources.discoverySpec?.enabled} /></Grid>
                                                {dataSource.details.dataSources.discoverySpec?.enabled && (
                                                    <>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="CSV Header Rows" value={dataSource.details.dataSources.discoverySpec.csvOptions?.headerRows} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="CSV Delimiter" value={dataSource.details.dataSources.discoverySpec.csvOptions?.delimiter} /></Grid>
                                                    </>
                                                )}
                                                <Grid item xs={12}><DetailItem label="Description" value={dataSource.details.dataSources.description} /></Grid>
                                            </Grid>
                                        </Box>

                                        {dataSource.status === 'rejected' && (
                                             <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <DetailItem label="Reason for Rejection" value={dataSource.reasonForRejection} />
                                             </Box>
                                        )}

                                    </Box>
                                    
                                    {dataSource?.status === "pending" && user.roles.some((role: string) => ["infrastructure_admin"].includes(role)) && (
                                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={3}>
                                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold" }} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
                                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "rgba(0,0,0,0.85)"}, textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained" size="large" onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                                        </Box>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
            </Grid>

            {/* Simple Confirmation Modal for Approval */}
            <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                        <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Data Source Request</Typography>
                        <IconButton aria-label="close" onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton>
                    </Box>
                    <Divider />
                    <Typography p={3}>Are you sure you want to approve this Data Source request?</Typography>
                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                        <Button type="submit" form="approve-dataSource-main-form" sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
                    </Box>
                </Box>
            </Modal>

            {/* Rejection Modal with its own Formik instance */}
            <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px", display: 'flex', flexDirection: 'column' }}>
                    <Formik
                        initialValues={{ reasonForRejection: "" }}
                        validationSchema={rejectValidationSchema}
                        onSubmit={async ({ reasonForRejection }) => {
                            if (!id) return;
                            try {
                                const rejectionData = await queryGraphql(
                                    `mutation RejectSolutionDataSources($input: RejectSolutionInput!) { rejectSolutionDataSources(input: $input) { id name } }`,
                                    { input: { id: id, reasonForRejection } }
                                );
                                if (rejectionData.rejectSolutionDataSources?.id) {
                                    setOpenRejectModal(false);
                                    navigate("/datasolutions/requests/data-sources");
                                }
                            } catch (error) { console.error("Error during rejection:", error); }
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                            <Form id="reject-dataSource-form" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                                    <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Data Source Request</Typography>
                                    <IconButton aria-label="close" onClick={() => setOpenRejectModal(false)}><Close fontSize="small" /></IconButton>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 3, flexGrow: 1 }}>
                                    <CompulsoryFieldsLabel fieldName="Comments" />
                                    <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                    <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                    <Button variant="outlined" onClick={() => setOpenRejectModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                    <Button type="submit" form="reject-dataSource-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained">Reject</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </Container>
    );
};

const DataSourcesApproveFormPage: React.FC = () => {
    const theme = useTheme();
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
    };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-data-sources"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <DataSourcesApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default DataSourcesApproveFormPage;



// import {
//     Avatar,
//     Box,
//     Breadcrumbs,
//     Button,
//     Container,
//     Divider,
//     Grid,
//     IconButton,
//     Link,
//     Modal,
//     TextField,
//     Tooltip,
//     Typography,
//     useTheme,
//     capitalize,
// } from "@mui/material";
// import React, { useState } from "react";
// import * as Yup from "yup";

// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { useNavigate, useParams } from "react-router-dom";
// import queryGraphql from "../../../helpers/ApiInterface";
// import { useAuth } from "../../../helpers/AuthProvider";
// import { Close } from "@mui/icons-material";
// import { Formik, ErrorMessage, Form } from "formik";

// import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
// import MiniBot from "../../MiniBot";
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";

// // --- Interfaces ---
// interface ApplicationInfo {
//     applicationName: string;
// }

// interface SolutionOwnerInfo {
//     name: string;
// }

// interface DataSourcesNestedDetails {
//     // User-submitted fields
//     projectId: string;
//     location: string;
//     lake: string;
//     zone: string;
//     zoneType: string;
//     description: string;
//     resourceSpec: {
//         type: string;
//         name: string;
//     };
//     discoverySpec: {
//         enabled: boolean;
//         csvOptions?: {
//             headerRows: number;
//             delimiter: string;
//         };
//     };
//     // Fields for approver input
//     network?: string;
//     subNetwork?: string;
//     serviceAccount?: string;
// }

// interface DataSourceDetailsData {
//     id: string;
//     name: string;
//     solutionCategoryType: string;
//     status: string;
//     cloudProvider: 'gcp' | 'aws' | 'azure' | string;
//     details: {
//         dataSources: DataSourcesNestedDetails; // Updated
//     };
//     solutionOwner: SolutionOwnerInfo;
//     application?: ApplicationInfo;
//     reasonForRejection?: string;
// }

// // --- Validation Schemas ---
// const approvalValidationSchema = Yup.object().shape({
//     network: Yup.string().required("This field is required"),
//     subNetwork: Yup.string().required("This field is required"),
//     serviceAccount: Yup.string().required("This field is required"),
// });

// const rejectValidationSchema = Yup.object().shape({
//     reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
// });

// // --- Helper Component ---
// const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
//     const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
//     return (
//         <Box mb={2}>
//             <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
//             <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
//         </Box>
//     );
// };

// // --- Main Content ---
// const DataSourcesApproveFormPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const { id } = useParams<{ id: string }>();
//     const { user } = useAuth();
//     const [dataSource, setDataSource] = useState<DataSourceDetailsData | null>(null);
//     const [solutionEndpoint, setSolutionEndpoint] = useState<string | null>(null);
//     const [openRejectModal, setOpenRejectModal] = useState(false);
//     const [openApproveModal, setOpenApproveModal] = useState(false);
//     const [dynamicLabels, setDynamicLabels] = useState({
//         projectId: 'Project ID',
//         network: 'Network',
//         subNetwork: 'Sub-Network'
//     });
//     const fallback = '-';

//     React.useEffect(() => {
//         const fetchData = async () => {
//             if (!id) return;
//             try {
//                 const data = await queryGraphql(
//                     `
//                     query GetDataSource($getDataSourceId: String!) {
//                       getDataSource(id: $getDataSourceId) {
//                         id, name, solutionCategoryType, status, reasonForRejection, cloudProvider
//                         details { 
//                           dataSources { 
//                             projectId, location, lake, zone, zoneType, description,
//                             resourceSpec { type, name },
//                             discoverySpec { enabled, csvOptions { headerRows, delimiter } },
//                           } 
//                         }
//                         solutionOwner { id, name }
//                         application { id, applicationName }
//                       }
//                     }
//                     `, { getDataSourceId: id }
//                 );
//                 if (data.getDataSource) {
//                     setDataSource(data.getDataSource);
//                 } else {
//                     console.log("Data Source details not found.");
//                     setDataSource(null);
//                 }
//             } catch (error) {
//                 console.log("Error fetching Data Source details:", error);
//             }
//         };
//         fetchData();
//     }, [id]);

//     React.useEffect(() => {
//         if (dataSource?.cloudProvider) {
//             switch (dataSource.cloudProvider) {
//                 case 'aws':
//                     setDynamicLabels({
//                         projectId: 'Account ID',
//                         network: 'VPC',
//                         subNetwork: 'Subnet'
//                     });
//                     break;
//                 case 'azure':
//                     setDynamicLabels({
//                         projectId: 'Subscription ID',
//                         network: 'Virtual Network (VNet)',
//                         subNetwork: 'Subnet'
//                     });
//                     break;
//                 case 'gcp':
//                 default:
//                     setDynamicLabels({
//                         projectId: 'Project ID',
//                         network: 'Network',
//                         subNetwork: 'Sub-Network'
//                     });
//                     break;
//             }
//         }
//     }, [dataSource]);


//     React.useEffect(() => {
//         const fetchEndpointUrl = async () => {
//             if (!dataSource?.id) return;
//             try {
//                 const data = await queryGraphql(
//                     `query ResourceStatus($resourceStatusId: String!) {
//                       resourceStatus(id: $resourceStatusId) { id, solutionEndpointUrl }
//                     }`, { resourceStatusId: dataSource.id }
//                 );
//                 if (data.resourceStatus?.solutionEndpointUrl) {
//                     setSolutionEndpoint(data.resourceStatus.solutionEndpointUrl);
//                 }
//             } catch (error) {
//                 console.log("Error fetching solution endpoint URL:", error);
//             }
//         };
//         fetchEndpointUrl();
//     }, [dataSource]);

//     const statusDisplay = dataSource?.status ? dataSource.status : "Status Unknown";
//     const statusBgColor = dataSource?.status === "active" || dataSource?.status === "approved" ? "#12B76A10" : dataSource?.status === "rejected" ? "#F0443810" : "#FDB02210";
//     const statusIcon = dataSource?.status === "active" || dataSource?.status === "approved" ? "/assets/tickcircle.svg" : dataSource?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg";

//     return (
//         <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
//             <MiniBot />
//             <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/datasolutions/requests/data-sources" onClick={(e) => { e.preventDefault(); navigate("/datasolutions/requests/data-sources") }}>
//                         Data Source Requests
//                     </Link>
//                     <Typography color="text.primary">Data Source Details</Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={1}>
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/requests/data-sources")} >
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">Data Source Details</Typography>
//                 </Box>
//             </Box>
//             <Grid container justifyContent="center">
//                 <Grid item xs={11} md={11} lg={11}>
//                     {dataSource && (
//                         <Formik
//                             enableReinitialize // Important for pre-populating form
//                             initialValues={{
//                                 network: dataSource.details.dataSources.network || "",
//                                 subNetwork: dataSource.details.dataSources.subNetwork || "",
//                                 serviceAccount: dataSource.details.dataSources.serviceAccount || "",
//                             }}
//                             validationSchema={approvalValidationSchema}
//                             onSubmit={async (values) => {
//                                 const detailsPayload: any = {
//                                     network: values.network,
//                                     subNetwork: values.subNetwork,
//                                     serviceAccount: values.serviceAccount,
//                                 };

//                                 try {
//                                     const approvalData = await queryGraphql(
//                                         `mutation ApproveSolutionDataSources($input: ApproveSolutionInput!) { approveSolutionDataSources(input: $input) { id name status } }`,
//                                         { input: { id: dataSource.id, details: { dataSources: detailsPayload } } }
//                                     );
//                                     if (approvalData.approveSolutionDataSources?.id) {
//                                         setOpenApproveModal(false);
//                                         navigate("/datasolutions/requests/data-sources");
//                                     } else {
//                                         console.error("Approval failed", approvalData);
//                                     }
//                                 } catch (error) {
//                                     console.error("Error during approval:", error);
//                                 }
//                             }}
//                         >
//                             {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                                 <Form id="approve-dataSource-main-form">
//                                     <Box my={2}>
//                                         <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
//                                             <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
//                                                 <Box sx={{ mb: { xs: 1, sm: 0 } }}>
//                                                     <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{dataSource.name || fallback}</Typography>
//                                                     <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${dataSource.solutionOwner?.name || fallback}`}</Typography>
//                                                 </Box>
//                                                 <Box textAlign="right">
//                                                     <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'flex-end' }}>
//                                                         <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                                         <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{ textTransform: 'capitalize' }}>{statusDisplay}</Typography>
//                                                     </Box>
//                                                 </Box>
//                                             </Box>
//                                         </Box>
                                        
//                                         <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
//                                             <Grid container spacing={2.5}>
//                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.projectId} value={dataSource.details.dataSources.projectId} /></Grid>
//                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Location" value={(dataSource.details.dataSources.location)} /></Grid>
//                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Lake" value={dataSource.details.dataSources.lake} /></Grid>
//                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Zone" value={dataSource.details.dataSources.zone} /></Grid>
//                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Zone Type" value={dataSource.details.dataSources.zoneType} /></Grid>
//                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Cloud Provider" value={dataSource.cloudProvider?.toUpperCase()} /></Grid>
//                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Application Name" value={dataSource.application?.applicationName} /></Grid>
//                                                 {solutionEndpoint && (<Grid item xs={12} sm={6} md={3}><Typography variant="caption" display="block" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}>Solution Endpoint</Typography><Tooltip title={<Link href={solutionEndpoint} target="_blank" rel="noopener noreferrer" sx={{ color: '#ffffff' }}>{solutionEndpoint}</Link>}><Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, cursor: 'pointer' }} /></Tooltip></Grid>)}
                                                
//                                                 <Grid item xs={12}><Divider/></Grid>
                                                
//                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Resource Type" value={dataSource.details.dataSources.resourceSpec?.type} /></Grid>
//                                                 <Grid item xs={12} sm={6} md={9}><DetailItem label="Resource Name" value={dataSource.details.dataSources.resourceSpec?.name} /></Grid>
//                                                 <Grid item xs={12} sm={6} md={3}><DetailItem label="Discovery" value={dataSource.details.dataSources.discoverySpec?.enabled} /></Grid>
//                                                 {dataSource.details.dataSources.discoverySpec?.enabled && (
//                                                     <>
//                                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="CSV Header Rows" value={dataSource.details.dataSources.discoverySpec.csvOptions?.headerRows} /></Grid>
//                                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="CSV Delimiter" value={dataSource.details.dataSources.discoverySpec.csvOptions?.delimiter} /></Grid>
//                                                     </>
//                                                 )}
//                                                 <Grid item xs={12}><DetailItem label="Description" value={dataSource.details.dataSources.description} /></Grid>
//                                             </Grid>
//                                         </Box>

//                                         {dataSource.status === 'pending' && (
//                                             <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
//                                                 <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
//                                                 <Divider sx={{ marginY: 2 }} />
//                                                 <Grid container spacing={2.5}>
//                                                     <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={dynamicLabels.network} /><TextField name="network" value={values.network} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${dynamicLabels.network}`} variant="filled" fullWidth hiddenLabel error={touched.network && Boolean(errors.network)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="network" /></div></Grid>
//                                                     <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={dynamicLabels.subNetwork} /><TextField name="subNetwork" value={values.subNetwork} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${dynamicLabels.subNetwork}`} variant="filled" fullWidth hiddenLabel error={touched.subNetwork && Boolean(errors.subNetwork)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="subNetwork" /></div></Grid>
//                                                     <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Service Account" /><TextField name="serviceAccount" value={values.serviceAccount} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Service Account" variant="filled" fullWidth hiddenLabel error={touched.serviceAccount && Boolean(errors.serviceAccount)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="serviceAccount" /></div></Grid>
//                                                 </Grid>
//                                             </Box>
//                                         )}
                                        
//                                         {dataSource.status === 'active' && (
//                                             <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
//                                                 <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
//                                                 <Divider sx={{ marginY: 2 }} />
//                                                 <Grid container spacing={2.5}>
//                                                     <Grid item xs={12} sm={6} md={4}><DetailItem label={dynamicLabels.network} value={dataSource.details.dataSources.network} /></Grid>
//                                                     <Grid item xs={12} sm={6} md={4}><DetailItem label={dynamicLabels.subNetwork} value={dataSource.details.dataSources.subNetwork} /></Grid>
//                                                     <Grid item xs={12} sm={6} md={4}><DetailItem label="Service Account" value={dataSource.details.dataSources.serviceAccount} /></Grid>
//                                                 </Grid>
//                                             </Box>
//                                         )}


//                                         {dataSource.status === 'rejected' && (
//                                              <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
//                                                 <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
//                                                 <Divider sx={{ marginY: 2 }} />
//                                                 <DetailItem label="Reason for Rejection" value={dataSource.reasonForRejection} />
//                                              </Box>
//                                         )}

//                                     </Box>
                                    
//                                     {dataSource?.status === "pending" && user.roles.some((role: string) => ["infrastructure_admin"].includes(role)) && (
//                                         <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={3}>
//                                             <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold" }} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
//                                             <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "rgba(0,0,0,0.85)"}, textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained" size="large" disabled={!isValid || !dirty} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
//                                         </Box>
//                                     )}
//                                 </Form>
//                             )}
//                         </Formik>
//                     )}
//                 </Grid>
//             </Grid>

//             {/* Simple Confirmation Modal for Approval */}
//             <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
//                 <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
//                     <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
//                         <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Data Source Request</Typography>
//                         <IconButton aria-label="close" onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton>
//                     </Box>
//                     <Divider />
//                     <Typography p={3}>Are you sure you want to approve this Data Source request?</Typography>
//                     <Divider />
//                     <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
//                         <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
//                         <Button type="submit" form="approve-dataSource-main-form" sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
//                     </Box>
//                 </Box>
//             </Modal>

//             {/* Rejection Modal with its own Formik instance */}
//             <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
//                 <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px", display: 'flex', flexDirection: 'column' }}>
//                     <Formik
//                         initialValues={{ reasonForRejection: "" }}
//                         validationSchema={rejectValidationSchema}
//                         onSubmit={async ({ reasonForRejection }) => {
//                             if (!id) return;
//                             try {
//                                 const rejectionData = await queryGraphql(
//                                     `mutation RejectSolutionDataSources($input: RejectSolutionInput!) { rejectSolutionDataSources(input: $input) { id name } }`,
//                                     { input: { id: id, reasonForRejection } }
//                                 );
//                                 if (rejectionData.rejectSolutionDataSources?.id) {
//                                     setOpenRejectModal(false);
//                                     navigate("/datasolutions/requests/data-sources");
//                                 }
//                             } catch (error) { console.error("Error during rejection:", error); }
//                         }}
//                     >
//                         {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                             <Form id="reject-dataSource-form" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
//                                 <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
//                                     <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Data Source Request</Typography>
//                                     <IconButton aria-label="close" onClick={() => setOpenRejectModal(false)}><Close fontSize="small" /></IconButton>
//                                 </Box>
//                                 <Divider />
//                                 <Box sx={{ p: 3, flexGrow: 1 }}>
//                                     <CompulsoryFieldsLabel fieldName="Comments" />
//                                     <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
//                                     <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
//                                 </Box>
//                                 <Divider />
//                                 <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
//                                     <Button variant="outlined" onClick={() => setOpenRejectModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
//                                     <Button type="submit" form="reject-dataSource-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained">Reject</Button>
//                                 </Box>
//                             </Form>
//                         )}
//                     </Formik>
//                 </Box>
//             </Modal>
//         </Container>
//     );
// };

// const DataSourcesApproveFormPage: React.FC = () => {
//     const theme = useTheme();
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//         if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
//     };
//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"requests-data-sources"} onStateChange={handleDrawerStateChange} />
//             <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
//                 <DataSourcesApproveFormPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default DataSourcesApproveFormPage;